interface DOMImageResizerOptions {
  onResize: (width: number, height: number) => void;
  onResizeEnd: (width: number, height: number) => void;
  onResizeStart?: () => void;
  initialWidth: number;
  initialHeight: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
}

export class DOMImageResizer {
  private container: HTMLDivElement;
  private isResizing = false;
  private resizeType = '';
  private startPos = { x: 0, y: 0 };
  private startSize = { width: 0, height: 0 };
  private startAspectRatio = 1;
  private currentWidth: number;
  private currentHeight: number;
  private options: DOMImageResizerOptions;

  constructor(options: DOMImageResizerOptions) {
    this.options = options;
    this.currentWidth = options.initialWidth;
    this.currentHeight = options.initialHeight;
    this.container = this.createResizerElement();
    this.setupEventListeners();
  }

  private createResizerElement(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'freeform-resizer-overlay';
    container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: ${this.currentWidth}px;
      height: ${this.currentHeight}px;
      pointer-events: none;
      z-index: 10;
    `;

    // Create resize handles
    const handles = [
      { type: 'nw', cursor: 'nw-resize', top: '-4px', left: '-4px' },
      { type: 'ne', cursor: 'ne-resize', top: '-4px', right: '-4px' },
      { type: 'sw', cursor: 'sw-resize', bottom: '-4px', left: '-4px' },
      { type: 'se', cursor: 'se-resize', bottom: '-4px', right: '-4px' },
      { type: 'n', cursor: 'n-resize', top: '-4px', left: '50%', transform: 'translateX(-50%)' },
      { type: 's', cursor: 's-resize', bottom: '-4px', left: '50%', transform: 'translateX(-50%)' },
      { type: 'w', cursor: 'w-resize', top: '50%', left: '-4px', transform: 'translateY(-50%)' },
      { type: 'e', cursor: 'e-resize', top: '50%', right: '-4px', transform: 'translateY(-50%)' },
    ];

    handles.forEach(handle => {
      const handleElement = document.createElement('div');
      handleElement.className = `resize-handle resize-handle-${handle.type}`;
      handleElement.style.cssText = `
        position: absolute;
        width: 8px;
        height: 8px;
        background-color: #3b82f6;
        border: 1px solid white;
        border-radius: 2px;
        cursor: ${handle.cursor};
        pointer-events: auto;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        transition: all 0.1s ease;
        ${handle.top ? `top: ${handle.top};` : ''}
        ${handle.bottom ? `bottom: ${handle.bottom};` : ''}
        ${handle.left ? `left: ${handle.left};` : ''}
        ${handle.right ? `right: ${handle.right};` : ''}
        ${handle.transform ? `transform: ${handle.transform};` : ''}
      `;

      handleElement.addEventListener('mouseenter', () => {
        handleElement.style.backgroundColor = '#2563eb';
        handleElement.style.transform = (handle.transform || '') + ' scale(1.2)';
      });

      handleElement.addEventListener('mouseleave', () => {
        handleElement.style.backgroundColor = '#3b82f6';
        handleElement.style.transform = handle.transform || '';
      });

      handleElement.addEventListener('mousedown', (e) => this.handleMouseDown(e, handle.type));
      container.appendChild(handleElement);
    });

    // Create selection border
    const border = document.createElement('div');
    border.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border: 2px solid #3b82f6;
      pointer-events: none;
    `;
    container.appendChild(border);

    return container;
  }

  private setupEventListeners(): void {
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
  }

  private handleMouseDown(e: MouseEvent, type: string): void {
    e.preventDefault();
    e.stopPropagation();

    this.isResizing = true;
    this.resizeType = type;
    this.startPos = { x: e.clientX, y: e.clientY };
    this.startSize = { width: this.currentWidth, height: this.currentHeight };
    
    // Capture the aspect ratio at the start of the drag
    this.startAspectRatio = this.currentHeight / this.currentWidth;

    // Call the onResizeStart callback if provided
    if (this.options.onResizeStart) {
      this.options.onResizeStart();
    }

    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }
    const deltaX = e.clientX - this.startPos.x;
    const deltaY = e.clientY - this.startPos.y;

    let newWidth = this.startSize.width;
    let newHeight = this.startSize.height;
    const { minWidth = 50, minHeight = 50, maxWidth = 1200, maxHeight = 1200 } = this.options;

    // Calculate new dimensions based on resize type
    switch (this.resizeType) {
      case 'nw': // Top-left corner - maintain aspect ratio
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.startSize.width - deltaX));
        newHeight = Math.round(newWidth * this.startAspectRatio);
        newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        // Recalculate width if height was constrained
        if (newHeight !== newWidth * this.startAspectRatio) {
          newWidth = Math.round(newHeight / this.startAspectRatio);
        }
        break;
      case 'ne': // Top-right corner - maintain aspect ratio
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.startSize.width + deltaX));
        newHeight = Math.round(newWidth * this.startAspectRatio);
        newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        // Recalculate width if height was constrained
        if (newHeight !== newWidth * this.startAspectRatio) {
          newWidth = Math.round(newHeight / this.startAspectRatio);
        }
        break;
      case 'sw': // Bottom-left corner - maintain aspect ratio
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.startSize.width - deltaX));
        newHeight = Math.round(newWidth * this.startAspectRatio);
        newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        // Recalculate width if height was constrained
        if (newHeight !== newWidth * this.startAspectRatio) {
          newWidth = Math.round(newHeight / this.startAspectRatio);
        }
        break;
      case 'se': // Bottom-right corner - maintain aspect ratio
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.startSize.width + deltaX));
        newHeight = Math.round(newWidth * this.startAspectRatio);
        newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));
        // Recalculate width if height was constrained
        if (newHeight !== newWidth * this.startAspectRatio) {
          newWidth = Math.round(newHeight / this.startAspectRatio);
        }
        break;
      case 'n': // Top edge - freeform height only
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.startSize.height - deltaY));
        break;
      case 's': // Bottom edge - freeform height only
        newHeight = Math.max(minHeight, Math.min(maxHeight, this.startSize.height + deltaY));
        break;
      case 'w': // Left edge - freeform width only
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.startSize.width - deltaX));
        break;
      case 'e': // Right edge - freeform width only
        newWidth = Math.max(minWidth, Math.min(maxWidth, this.startSize.width + deltaX));
        break;
    }

    this.currentWidth = newWidth;
    this.currentHeight = newHeight;

    // Update the resizer container size
    this.container.style.width = `${newWidth}px`;
    this.container.style.height = `${newHeight}px`;

    // Call the resize callback
    this.options.onResize(newWidth, newHeight);
  }

  private handleMouseUp(): void {
    if (this.isResizing) {
      this.isResizing = false;
      this.resizeType = '';

      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);

      // Call the resize end callback
      this.options.onResizeEnd(this.currentWidth, this.currentHeight);
    }
  }

  public getElement(): HTMLDivElement {
    return this.container;
  }

  public updateSize(width: number, height: number): void {
    this.currentWidth = width;
    this.currentHeight = height;
    this.container.style.width = `${width}px`;
    this.container.style.height = `${height}px`;
  }

  public destroy(): void {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}
